import { getOrderedMatchedBadges, mergeAttributesRawForBadging } from '@/helpers/utils/productBadging';
import type {
  ProductAttributeRaw,
  ProductBadgingEntry,
  ProductBadgingUtilInput,
  ProductCategoryForBadging,
  BadgeConfiguration,
} from '@/helpers/utils/productBadging';

export function isPlBadgeConfigurationsEnabled(value: unknown): boolean {
  if (value === true || value === 1) return true;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === 'true' || normalized === '1' || normalized === 'yes';
  }
  return false;
}

/** Same slug normalization as PDP category rules (`_` segment before site suffix). */
function normalizePlCategorySegment(slug: string): string {
  const s = slug.trim().toLowerCase();
  return s.split('_')[0] || s;
}

/**
 * Maps Algolia category paths into the same tree shape `getOrderedMatchedBadges` expects.
 *
 * When `hierarchicalCategories` has paths, we use **only** those (site nav tree). That avoids
 * extra slugs from `categoryURLFilter` (e.g. `...|all`) that CommerceTools / PDP categories
 * typically do not include — which otherwise makes PLP match category rules PDP does not.
 * If hierarchical data is absent, we fall back to `categoryURLFilter`.
 */
export function algoliaHitToProductCategoriesForBadging(hit: any): ProductCategoryForBadging[] | undefined {
  const out: ProductCategoryForBadging[] = [];
  const addPath = (path: string) => {
    const parts = String(path)
      .split('|')
      .map((p) => p.trim())
      .filter(Boolean);
    if (!parts.length) return;
    const leaf = normalizePlCategorySegment(parts[parts.length - 1]);
    const ancestors = parts.slice(0, -1).map((p) => ({ slug: normalizePlCategorySegment(p) }));
    out.push({ slug: leaf, ancestors });
  };

  const hc = hit?.hierarchicalCategories;
  const hcLevels = ['level0', 'level1', 'level2', 'level3', 'level4'] as const;
  const hasHierarchicalPaths =
    hc &&
    typeof hc === 'object' &&
    hcLevels.some((lvl) => {
      const arr = hc[lvl];
      return Array.isArray(arr) && arr.length > 0;
    });

  if (hasHierarchicalPaths) {
    hcLevels.forEach((lvl) => {
      const arr = hc[lvl];
      if (Array.isArray(arr)) arr.forEach((path: string) => addPath(path));
    });
  } else if (Array.isArray(hit?.categoryURLFilter)) {
    hit.categoryURLFilter.forEach((path: string) => addPath(path));
  }

  return out.length ? out : undefined;
}

function normalizeAlgoliaAttributeKey(key: string): string {
  const lower = key.toLowerCase();
  if (lower === 'overridebadgelabels') return 'overrideBadgeLabels';
  return key;
}

const OVERRIDE_BADGE_LABELS_NAME = 'overridebadgelabels';

function isOverrideBadgeLabelsAttributeRow(row: ProductAttributeRaw | undefined): boolean {
  return (
    String(row?.name ?? '')
      .trim()
      .toLowerCase() === OVERRIDE_BADGE_LABELS_NAME
  );
}

/**
 * When PLP size/width facets resolve a `variantDetails` row with `overrideBadgeLabels`, that list
 * must win over top-level hit/swatch `overrideBadgeLabels` for CMS badge filtering (same precedence
 * as `parseOverrideBadgeLabelKeys` first match).
 */
export function mergeFacetVariantOverrideBadgeLabels(
  mergedAttributesRaw: ProductAttributeRaw[],
  facetVariantOverrideBadgeLabels: string[],
): ProductAttributeRaw[] {
  if (!Array.isArray(facetVariantOverrideBadgeLabels) || facetVariantOverrideBadgeLabels.length === 0) {
    return mergedAttributesRaw;
  }
  const filtered = mergedAttributesRaw.filter((a) => !isOverrideBadgeLabelsAttributeRow(a));
  return [{ name: 'overrideBadgeLabels', value: [...facetVariantOverrideBadgeLabels] }, ...filtered];
}

/** Every own key on the Algolia record (hit or swatch) as a synthetic attribute for CMS rules. */
function buildSyntheticAttributesFromAlgoliaSource(source: any): ProductAttributeRaw[] {
  if (!source || typeof source !== 'object') return [];
  const rows: ProductAttributeRaw[] = [];
  for (const key of Object.keys(source)) {
    const value = source[key];
    if (value === undefined || value === null) continue;
    if (typeof value === 'function') continue;
    rows.push({ name: normalizeAlgoliaAttributeKey(key), value });
  }
  return rows;
}

/**
 * Builds `ProductBadgingUtilInput` from an Algolia PLP hit + optional selected swatch.
 * Swatch attributes are merged after the hit so swatch-specific values win on duplicate names.
 */
export function buildPlProductBadgingUtilInput(options: {
  hit: any;
  selectedSwatch: any | null;
  productBadgingEntries: ProductBadgingEntry[] | null | undefined;
  badgeConfigurations: unknown;
  /** Same semantics as PDP: finite number > 0 caps `getOrderedMatchedBadges`; omit / invalid = no cap. */
  maxVisibleBadges?: number;
  /**
   * Optional: facet-matched `variantDetails` row `overrideBadgeLabels` (PLP size/width refinements).
   * When non-empty, replaces any `overrideBadgeLabels` from merged hit/swatch synthetic attributes.
   */
  facetVariantOverrideBadgeLabels?: string[] | null;
}): ProductBadgingUtilInput {
  const {
    hit,
    selectedSwatch,
    productBadgingEntries,
    badgeConfigurations,
    maxVisibleBadges,
    facetVariantOverrideBadgeLabels,
  } = options;
  const hitAttrs = buildSyntheticAttributesFromAlgoliaSource(hit);
  const swatchAttrs = selectedSwatch ? buildSyntheticAttributesFromAlgoliaSource(selectedSwatch) : [];
  const mergedAttributesRaw = mergeAttributesRawForBadging(hitAttrs, swatchAttrs);
  const withFacetOverride =
    Array.isArray(facetVariantOverrideBadgeLabels) && facetVariantOverrideBadgeLabels.length > 0
      ? mergeFacetVariantOverrideBadgeLabels(mergedAttributesRaw, facetVariantOverrideBadgeLabels)
      : mergedAttributesRaw;

  return {
    productBadgingEntries: Array.isArray(productBadgingEntries) ? productBadgingEntries : undefined,
    badgeConfigurations: Array.isArray(badgeConfigurations) ? (badgeConfigurations as any) : undefined,
    productCategories: algoliaHitToProductCategoriesForBadging(hit),
    initialColorKey: selectedSwatch?.key ?? hit?.key ?? null,
    initialSizeKey: selectedSwatch?.sku ?? hit?.sku ?? null,
    attributesRaw: withFacetOverride.length ? withFacetOverride : undefined,
    maxVisibleBadges,
  };
}

/** Ordered CMS rule badges for PLP (same engine as PDP). Sync; prefer `schedulePlRuleBadges` on the main thread. */
export function computePlOrderedRuleBadges(input: ProductBadgingUtilInput): BadgeConfiguration[] {
  return getOrderedMatchedBadges(input);
}

export function schedulePlRuleBadges(
  input: ProductBadgingUtilInput,
  onResult: (badges: BadgeConfiguration[]) => void,
): () => void {
  const run = () => {
    try {
      onResult(computePlOrderedRuleBadges(input));
    } catch {
      onResult([]);
    }
  };

  let idleId: number | undefined;
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  if (typeof window === 'undefined') {
    queueMicrotask(run);
    return () => {};
  }
  if ('requestIdleCallback' in window) {
    idleId = window.requestIdleCallback(run, { timeout: 2000 });
  } else {
    timeoutId = globalThis.setTimeout(run, 0);
  }

  return () => {
    if (idleId != null && 'cancelIdleCallback' in window) {
      window.cancelIdleCallback(idleId);
    }
    if (timeoutId != null) {
      globalThis.clearTimeout(timeoutId);
    }
  };
}

/** Stable key for scheduling when Algolia hit / swatch / rules inputs change. */
export function getPlBadgeComputationSignature(options: {
  hit: any;
  selectedSwatch: any | null;
  productBadgingEntries: ProductBadgingEntry[] | null | undefined;
  badgeConfigurationsJson: string;
  /** Include when `maxBadgesPlp` affects `getOrderedMatchedBadges` output. */
  maxBadgesPlpKey?: string;
  /** PLP refinement state + facet-derived override badge keys (recompute when facets / variant row change). */
  plpFacetBadgeSignature?: string;
}): string {
  const {
    hit,
    selectedSwatch,
    productBadgingEntries,
    badgeConfigurationsJson,
    maxBadgesPlpKey,
    plpFacetBadgeSignature,
  } = options;
  const entriesLen = Array.isArray(productBadgingEntries) ? productBadgingEntries.length : -1;
  return [
    hit?.objectID ?? hit?.id ?? '',
    selectedSwatch?.key ?? '',
    hit?.key ?? '',
    hit?.sku ?? '',
    entriesLen,
    badgeConfigurationsJson,
    maxBadgesPlpKey ?? '',
    plpFacetBadgeSignature ?? '',
  ].join('::');
}
